const DATE_ONLY_FIELD_REGEX = /^(\d{4})[-/](\d{2})[-/](\d{2})(?:T.*)?$/;

/** Converts a calendar date in DD/MM/YYYY format to ISO without a time zone. */
export function convertDisplayDateToIsoDateOnly(value: string): string | null {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value.trim());
  if (!match) return null;

  const [, dayText, monthText, yearText] = match;
  const day = Number(dayText);
  const month = Number(monthText);
  const year = Number(yearText);
  if (year < 1 || month < 1 || month > 12 || day < 1) return null;

  const isLeapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const daysInMonth = [31, isLeapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  if (day > daysInMonth[month - 1]) return null;

  return `${yearText}-${monthText}-${dayText}`;
}

export interface DateOnlyParts {
  year: number;
  month: number;
  day: number;
}

function padDateOnlyPart(value: number): string {
  return String(value).padStart(2, '0');
}

function formatDateOnlyParts(parts: DateOnlyParts): string {
  return `${padDateOnlyPart(parts.day)}/${padDateOnlyPart(parts.month)}/${parts.year}`;
}

export function extractDateOnlyParts(value: string): DateOnlyParts | null {
  const match = DATE_ONLY_FIELD_REGEX.exec(value.trim());
  if (!match) {
    return null;
  }

  const [, year, month, day] = match;
  return {
    year: Number.parseInt(year, 10),
    month: Number.parseInt(month, 10),
    day: Number.parseInt(day, 10),
  };
}

export function parseDateOnlyFieldValue(value: string): Date | undefined {
  const parts = extractDateOnlyParts(value);
  if (!parts) {
    return undefined;
  }

  const date = new Date(parts.year, parts.month - 1, parts.day, 12, 0, 0, 0);
  return Number.isNaN(date.getTime()) ? undefined : date;
}

export function formatDateOnlyFieldValue(value: Date | string): string | undefined {
  if(!value) return;
  
  if (value instanceof Date) {
    return Number.isNaN(value.getTime())
      ? undefined
      : formatDateOnlyParts({
          year: value.getFullYear(),
          month: value.getMonth() + 1,
          day: value.getDate(),
        });
  }

  const parts = extractDateOnlyParts(value);
  if (parts) {
    return formatDateOnlyParts(parts);
  }

  const parsedDate = new Date(value);
  return Number.isNaN(parsedDate.getTime()) ? undefined : formatDateOnlyFieldValue(parsedDate);
}
